/**
 * Reusable utilities for formatting sales data consistently across the application.
 * Provides methods for currency formatting, payment display with counts, and amount comparisons.
 */

/**
 * Format a currency amount using locale-specific formatting
 * @param amount - The numeric amount to format
 * @param currency - Currency code (default: 'AUD')
 * @returns Formatted currency string
 */
export const formatCurrencyAmount = (amount: number, currency: string = 'AUD'): string => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: currency,
  }).format(amount)
}

/**
 * Format a payment amount with optional count display
 * @param amount - Payment amount
 * @param count - Optional number of payments
 * @returns Formatted string with amount and count if provided
 */
export const formatPaymentWithCount = (amount: number, count?: number): string => {
  const currencyAmount = formatCurrencyAmount(amount)
  // Use non-breaking space (\u00A0) between amount and count for better display
  return count && count > 0 ? `${currencyAmount}\u00A0(${count})` : currencyAmount
}

/**
 * Check if two amounts match within a tolerance
 * @param lsAmount - Lightspeed amount
 * @param xeroAmount - Xero amount
 * @param tolerance - Maximum allowed difference (default 0.01 for cents)
 * @returns Boolean indicating if amounts match
 */
export const getAmountMatchStatus = (
  lsAmount?: number | null,
  xeroAmount?: number | null,
  tolerance: number = 0.01
): boolean => {
  // If either is null/undefined, they don't match (unless both are null)
  if (lsAmount == null || xeroAmount == null) {
    return lsAmount === xeroAmount
  }

  // Check if amounts are within tolerance
  return Math.abs(lsAmount - xeroAmount) <= tolerance
}

/**
 * Format a client name from customer data
 * @param customer - Customer data object
 * @returns Formatted customer name string
 */
export const formatClientName = (customer: {
  customer_first_name?: string
  customer_last_name?: string
  customer_company?: string
  customer_id?: string
}): string => {
  const parts = []
  if (customer.customer_first_name) parts.push(customer.customer_first_name)
  if (customer.customer_last_name) parts.push(customer.customer_last_name)
  if (customer.customer_company) parts.push(`(${customer.customer_company})`)

  return parts.length > 0 ? parts.join(' ') : customer.customer_id || 'N/A'
}

/**
 * Get background color for mismatched fields
 * @param isMatch - Whether the values match
 * @returns CSS background color or undefined
 */
export const getMismatchBackgroundColor = (isMatch?: boolean): string | undefined => {
  return isMatch === false ? 'rgba(244, 67, 54, 0.08)' : undefined
}

/**
 * Get border radius for mismatched fields
 * @param isMatch - Whether the values match
 * @returns CSS border radius or undefined
 */
export const getMismatchBorderRadius = (isMatch?: boolean): number | undefined => {
  return isMatch === false ? 1 : undefined
}

/**
 * Format amount value for display, handling null/undefined
 * @param amount - Amount to format
 * @returns Formatted amount or '-' if null/undefined
 */
export const formatAmountDisplay = (amount?: number | null): string => {
  if (amount == null) return '-'
  return formatCurrencyAmount(amount)
}

/**
 * Format payment display with count, handling null/undefined
 * @param amount - Payment amount
 * @param count - Payment count
 * @returns Formatted payment string or '-' if amount is null/undefined
 */
export const formatPaymentDisplay = (amount?: number | null, count?: number | null): string => {
  if (amount == null) return '-'
  return formatPaymentWithCount(amount, count ?? undefined)
}

/**
 * Effective Xero settlement on an invoice: cash payments + credit-note
 * allocations applied. Mirrors the backend xero_settled_total helper and
 * XERO_SETTLED_TOTAL_SQL fragment in shared/sale_alignment.py - any future
 * "payments" column (Sales Data Explorer, ad-hoc reports) should reach for
 * this so the credit-aware math lives in one place.
 */
export const xeroSettledTotal = (
  amountPaid?: number | null,
  amountCredited?: number | null,
): number => (amountPaid ?? 0) + (amountCredited ?? 0)

const roundToCents = (n: number | null | undefined): number =>
  n == null ? 0 : Math.round(n * 100) / 100

/**
 * Pick the Xero payments figure that agrees with LS under the credit-aware
 * OR, mirroring SALE_MISMATCHES_CTE_BODY's payments_match branches in
 * backend/shared/sale_alignment.py. Use this whenever feeding Xero payments
 * into collectSaleMismatches so the popup, the table-row chip-strip, and
 * the backend tagger all reach the same verdict on what's reconciled.
 *
 * Branch 1 (no-credit / cash-only): LS == amount_paid -> return amount_paid.
 * Branch 2 (credit-aware): LS == amount_paid + amount_credited -> return settled.
 * Branch 3 (credit-note pair): LS records no outgoing refund (≈ 0) and the
 *   credit note is fully settled (applied_amount >= total at 2dp) -> echo
 *   LS so the diff is zero. Mirrors the third limb of payments_match;
 *   partial / unallocated / stray-LS-payments cases return total so a real
 *   diff still flags.
 * Otherwise: fall back to amount_paid so a real diff still flags.
 *
 * For credit-note pairs, callers should pass ``xeroAmountPaid`` =
 * ABS(xcn.applied_amount) and ``xeroCreditNoteTotal`` = ABS(xcn.total),
 * matching ``pair_xero_counterpart``'s ABS-normalisation.
 */
export const xeroPaymentsForMatch = (
  lsPaymentsTotal: number | null | undefined,
  xeroAmountPaid: number | null | undefined,
  xeroAmountCredited: number | null | undefined,
  hasXeroCredit: boolean | null | undefined,
  xeroKind?: 'invoice' | 'credit_note' | 'missing' | null,
  xeroCreditNoteTotal?: number | null,
): number => {
  const ls = roundToCents(lsPaymentsTotal)
  if (xeroKind === 'credit_note') {
    const total = roundToCents(xeroCreditNoteTotal)
    const applied = roundToCents(xeroAmountPaid)
    if (ls === 0 && total > 0 && applied >= total) return 0
    return total > 0 ? total : (xeroAmountPaid ?? 0)
  }
  const settled = xeroSettledTotal(xeroAmountPaid, xeroAmountCredited)
  if (hasXeroCredit && ls === roundToCents(settled)) return settled
  return xeroAmountPaid ?? 0
}

/**
 * Signed-delta string for a compare-row chip: '+$1.23' / '-$1.23' for
 * currency, '+1.5' / '-2' for plain numbers. Returns "" sign on 0 so
 * the caller can render an empty chip. Single source of truth for the
 * inspector table (via formatCurrencyDelta / formatIntDelta below) and
 * the side-by-side dialog (CompareSalesView), so both read identically.
 */
export const formatSignedDelta = (diff: number, isCurrency: boolean): string => {
  const sign = diff > 0 ? '+' : diff < 0 ? '-' : ''
  const abs = Math.abs(diff)
  if (isCurrency) return `${sign}${formatCurrencyAmount(abs)}`
  // Round to 1dp for fractional quantities (line item qty); whole numbers
  // render without a trailing ".0".
  const rounded = Math.round(abs * 10) / 10
  return `${sign}${rounded}`
}

/**
 * Signed currency delta vs Lightspeed for the Xero side of a compare row.
 * Both sides ABS-normalised so credit-note pairs (LS negative, Xero positive)
 * compare by magnitude. Returns null when within rounding (< $0.005) so the
 * caller can hide the chip when there's nothing to flag. Format: '+$1.23' /
 * '-$1.23'.
 */
export const formatCurrencyDelta = (
  ls: number | null | undefined,
  xero: number | null | undefined,
): string | null => {
  if (ls == null || xero == null) return null
  const diff = Math.abs(xero) - Math.abs(ls)
  if (Math.abs(diff) < 0.005) return null
  return formatSignedDelta(diff, true)
}

/**
 * Signed integer delta vs Lightspeed for the Xero side of a compare row
 * (line counts, payment counts). Returns null when equal so the chip stays
 * hidden when there's nothing to flag.
 */
export const formatIntDelta = (
  ls: number | null | undefined,
  xero: number | null | undefined,
): string | null => {
  if (ls == null || xero == null) return null
  const diff = (xero || 0) - (ls || 0)
  if (diff === 0) return null
  return formatSignedDelta(diff, false)
}